﻿(function () {
  'use strict';

  if (window.DFN_COLLECTION_FITMENT_BOOTSTRAPPED) return;
  window.DFN_COLLECTION_FITMENT_BOOTSTRAPPED = true;

  const API_BASE = '/apps/tecdoc';
  const STORAGE_KEY = 'dfn_vehicle';
  const REQUEST_CACHE_PREFIX = 'dfn_collection_fitment_cache:';
  const REQUEST_CACHE_TTL = 15 * 60 * 1000;
  const COLLECTION_ROOT_SELECTOR = '[data-dfn-collection-fitment="true"]';
  const FITMENT_ROOT_SELECTOR = '[data-dfn-collection-fitment="true"], [data-dfn-fitment-root="true"]';
  const CARD_SELECTOR = '[data-dfn-product-card], product-card.card--product';
  const BADGE_SELECTOR = '[data-dfn-card-fitment-badge]';
  const compatibilityCache = new Map();
  const familyCache = new Map();
  const scheduledRoots = new WeakSet();
  const requestCache = new Map();
  const inflightRequests = new Map();

  function qsa(root, selector) {
    return root ? Array.from(root.querySelectorAll(selector)) : [];
  }

  function esc(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function readSessionCache(key) {
    try {
      const raw = sessionStorage.getItem(`${REQUEST_CACHE_PREFIX}${key}`);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || parsed.expiresAt <= Date.now()) {
        sessionStorage.removeItem(`${REQUEST_CACHE_PREFIX}${key}`);
        return null;
      }

      return parsed.value;
    } catch (_) {
      return null;
    }
  }

  function writeSessionCache(key, value, ttl) {
    try {
      sessionStorage.setItem(`${REQUEST_CACHE_PREFIX}${key}`, JSON.stringify({
        value,
        expiresAt: Date.now() + ttl
      }));
    } catch (_) {}
  }

  function normalizeSku(sku) {
    const value = String(sku || '').trim();
    if (!value) return '';
    return value
      .replace(/-\d+(?:-\d+)?(?:_\d+)?$/i, '')
      .replace(/_\d+$/i, '')
      .toUpperCase();
  }

  function skuAliases(sku) {
    const normalized = normalizeSku(sku);
    if (!normalized) return [];

    const aliases = new Set([normalized]);
    ['SL', 'T'].forEach((prefix) => {
      if (!normalized.startsWith(prefix)) return;

      const withoutPrefix = normalized.slice(prefix.length);
      if (withoutPrefix.length >= 4 && /\d/.test(withoutPrefix)) {
        aliases.add(withoutPrefix);
      }
    });

    return Array.from(aliases);
  }

  function skuLikeAliases(value) {
    return String(value || '')
      .toUpperCase()
      .split('?')[0]
      .split('#')[0]
      .split(/[^A-Z0-9]+/)
      .filter((token) => token.length >= 6 && token.length <= 32 && /\d{5,}/.test(token))
      .flatMap(skuAliases);
  }

  function normalizeHandle(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return '';

    const withoutQuery = normalized.split('?')[0].split('#')[0];
    const marker = '/products/';
    const markerIndex = withoutQuery.indexOf(marker);
    return markerIndex >= 0
      ? withoutQuery.slice(markerIndex + marker.length).replace(/\/+$/g, '')
      : withoutQuery.replace(/^\/+|\/+$/g, '');
  }

  function cardSkuAliases(card) {
    const values = [
      card && card.dataset ? card.dataset.dfnCardSku : '',
      ...String(card && card.dataset ? card.dataset.dfnCardSkus || '' : '')
        .split('|')
    ];

    qsa(card, 'a[href], img[src], img[alt]').forEach((node) => {
      values.push(node.getAttribute('href') || '');
      values.push(node.getAttribute('src') || '');
      values.push(node.getAttribute('alt') || '');
    });

    return Array.from(new Set(values.flatMap((value) => [
      ...skuAliases(value),
      ...skuLikeAliases(value)
    ])));
  }

  function cardHandles(card) {
    const handles = new Set();
    const datasetHandle = normalizeHandle(card && card.dataset ? card.dataset.dfnCardHandle : '');
    if (datasetHandle) handles.add(datasetHandle);

    qsa(card, 'a[href*="/products/"]').forEach((link) => {
      const handle = normalizeHandle(link.getAttribute('href') || '');
      if (handle) handles.add(handle);
    });

    return Array.from(handles);
  }

  function findOrCreateBadge(card) {
    const badges = qsa(card, BADGE_SELECTOR);
    const existing = badges[0];
    badges.slice(1).forEach((badge) => badge.remove());
    if (existing) return existing;

    const info = card.querySelector('.card__info-inner') || card.querySelector('.card__info') || card;
    const badge = document.createElement('div');
    badge.className = 'dfn-card-fitment';
    badge.setAttribute('data-dfn-card-fitment-badge', '');
    badge.hidden = true;

    const title = info.querySelector('.card__title');
    if (title && title.nextSibling) {
      info.insertBefore(badge, title.nextSibling);
    } else if (title) {
      info.appendChild(badge);
    } else {
      info.appendChild(badge);
    }

    return badge;
  }

  function resolveCardFromLink(link) {
    return link.closest('[data-dfn-product-card]')
      || link.closest('product-card')
      || link.closest('.card')
      || link.closest('.slider__item')
      || link.closest('li');
  }

  function isPreferredNestedCard(parent, child) {
    return parent !== child
      && parent.contains(child)
      && (
        child.matches('[data-dfn-product-card], product-card, .card')
        || child.querySelector('a[href*="/products/"]')
      );
  }

  function getSavedVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function vehicleId(vehicle) {
    return vehicle && (vehicle.vehicleId || vehicle.id || vehicle.Id);
  }

  function getConfig(root, vehicle) {
    const dataset = root && root.dataset ? root.dataset : {};

    return {
      storeId: dataset.storeId || (vehicle && vehicle.storeId) || 'main-store',
      profile: dataset.tecDocProfile || (vehicle && vehicle.tecDocProfile) || 'de',
      language: dataset.language || document.documentElement.lang || 'de',
      label: dataset.compatibleLabel || 'Passt zu Ihrem Fahrzeug',
      currentFamilyLabel: dataset.currentFamilyLabel || 'Nur passende {family} anzeigen',
      moreCategoriesLabel: dataset.moreCategoriesLabel || 'Weitere passende Kategorien',
      allPartsLabel: dataset.allPartsLabel || 'Alle passenden Teile',
      familyAllResultsLabel: dataset.familyAllResultsLabel || 'Results',
      productSingularLabel: dataset.productSingularLabel || 'Produkt',
      productPluralLabel: dataset.productPluralLabel || 'Produkte',
      currentFamilyName: dataset.currentFamilyName || '',
      currentCollection: dataset.currentCollection || '',
      resultsPageUrl: dataset.resultsPageUrl || '/pages/fahrzeugergebnisse'
    };
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function formatText(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, (_, key) => {
      const value = values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : '';
      return value == null ? '' : String(value);
    });
  }

  function countLabel(config, count) {
    const value = Number(count || 0);
    return `${value} ${value === 1 ? config.productSingularLabel : config.productPluralLabel}`;
  }

  function currentFamilySlug(config) {
    const current = normalize(config.currentCollection);
    const knownFamilies = new Set(['einspritzdusen', 'turbolader', 'nox-sensoren', 'dichtringe']);
    return knownFamilies.has(current) ? current : '';
  }

  function currentFamilyName(config, familySlug) {
    const configured = String(config && config.currentFamilyName || '').trim();
    if (configured) return configured;

    const normalized = String(familySlug || '').trim().replace(/-/g, ' ');
    return normalized || 'Teile';
  }

  function currentLocalePrefix() {
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
    const reserved = new Set(['apps', 'account', 'cart', 'collections', 'pages', 'products', 'search']);
    if (!firstSegment || reserved.has(firstSegment.toLowerCase())) return '';
    return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstSegment) ? `/${firstSegment}` : '';
  }

  function localizeStorefrontPath(value) {
    const raw = String(value || '').trim();
    if (!raw || raw === '#') return raw || '#';

    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return raw;

      const prefix = currentLocalePrefix();
      if (!prefix || url.pathname.startsWith(`${prefix}/`) || url.pathname === prefix) {
        return `${url.pathname}${url.search}${url.hash}`;
      }

      if (/^\/(?:products|collections|pages|search)(?:\/|\?|$)/.test(url.pathname)) {
        return `${prefix}${url.pathname}${url.search}${url.hash}`;
      }

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return raw;
    }
  }

  function buildVehicleResultsUrl(config, vehicle, family) {
    const id = vehicleId(vehicle);
    const url = new URL(config.resultsPageUrl || '/pages/fahrzeugergebnisse', window.location.origin);

    if (id) url.searchParams.set('vehicleId', String(id));
    url.searchParams.set('storeId', config.storeId);
    url.searchParams.set('tecDocProfile', config.profile);

    const selectedFamily = String(family || '').trim();
    if (selectedFamily) {
      url.searchParams.set('family', selectedFamily);
    } else {
      url.searchParams.delete('family');
    }

    return localizeStorefrontPath(`${url.pathname}${url.search}`);
  }

  function vehicleName(vehicle) {
    return [
      vehicle && (vehicle.makeName || vehicle.MakeName || ''),
      vehicle && (vehicle.modelName || vehicle.ModelName || ''),
      vehicle && (vehicle.typeName || vehicle.TypeName || '')
    ].filter(Boolean).join(' ');
  }

  function vehicleMeta(vehicle) {
    return [
      vehicle && (vehicle.engineCode || vehicle.EngineCode || ''),
      vehicle && (vehicle.powerHp || vehicle.PowerHp) ? `${vehicle.powerHp || vehicle.PowerHp} PS` : '',
      vehicle && (vehicle.yearFrom || vehicle.YearFrom)
        ? `${vehicle.yearFrom || vehicle.YearFrom}${vehicle.yearTo || vehicle.YearTo ? `-${vehicle.yearTo || vehicle.YearTo}` : ''}`
        : ''
    ].filter(Boolean).join(' - ');
  }

  function vehicleDisplayName(vehicle) {
    return [vehicleName(vehicle), vehicleMeta(vehicle)].filter(Boolean).join(' - ');
  }

  function vehicleImage(vehicle) {
    return vehicle && (vehicle.imageUrl || vehicle.ImageUrl || '');
  }

  function proxyVehicleImage(url) {
    const value = String(url || '').trim();
    if (!value) return '';
    if (value.startsWith('/')) return value;
    return `${API_BASE}/vehicle-image?url=${encodeURIComponent(value)}`;
  }

  function firstLetter(value) {
    const normalized = String(value || '').trim();
    return normalized ? normalized.charAt(0).toUpperCase() : 'F';
  }

  function hideCollectionVehicleContext(root) {
    const context = root && root.querySelector('[data-dfn-collection-vehicle-context]');
    if (!context) return;

    context.hidden = true;
    context.classList.add('dfn-hidden');
    hideCollectionFamilyNav(root);
  }

  function hideCollectionFamilyNav(root) {
    const nav = root && root.querySelector('[data-dfn-collection-family-nav]');
    if (!nav) return;

    nav.innerHTML = '';
    nav.hidden = true;
    nav.classList.add('dfn-hidden');
  }

  function openFinderFromCollection() {
    document.dispatchEvent(new CustomEvent('dfn:open-finder'));

    window.setTimeout(() => {
      const target = document.querySelector('[data-dfn-header-shell="true"]')
        || document.querySelector('[data-dfn-finder-root="true"]')
        || document.querySelector('.dfn-locator');

      const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
      if (!isMobile && target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      const trigger = target && (
        target.querySelector('[data-dfn-role="header-open"]')
        || target.querySelector('[data-dfn-role="header-change"]')
      );
      if (trigger) trigger.click();

      const input = target && (
        target.querySelector('[data-dfn-role="header-plate-input"]')
        || target.querySelector('[data-dfn-role="header-hsn-input"]')
        || target.querySelector('[data-dfn-role="make-select"]')
        || target.querySelector('select, input')
      );
      if (input && typeof input.focus === 'function') {
        input.focus({ preventScroll: true });
      }
    }, 0);
  }

  function clearSavedVehicle() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}

    document.dispatchEvent(new CustomEvent('dfn:vehicle-cleared'));
  }

  function renderCollectionVehicleContext(root, vehicle, config) {
    const context = root && root.querySelector('[data-dfn-collection-vehicle-context]');
    if (!context) return;

    if (!vehicleMatchesConfig(vehicle, config)) {
      hideCollectionVehicleContext(root);
      return;
    }

    const displayName = vehicleDisplayName(vehicle);
    const imageSrc = proxyVehicleImage(vehicleImage(vehicle));
    const nameEl = context.querySelector('[data-dfn-collection-vehicle-name]');
    const imageEl = context.querySelector('[data-dfn-collection-vehicle-image]');
    const fallbackEl = context.querySelector('[data-dfn-collection-vehicle-fallback]');
    const changeButton = context.querySelector('[data-dfn-collection-vehicle-change]');
    const clearButton = context.querySelector('[data-dfn-collection-vehicle-clear]');
    const currentFamilyLink = context.querySelector('[data-dfn-collection-current-family-link]');

    if (nameEl) nameEl.textContent = displayName;
    if (fallbackEl) fallbackEl.textContent = firstLetter(vehicle.makeName || vehicle.MakeName || displayName);

    if (imageEl) {
      if (imageSrc) {
        imageEl.src = imageSrc;
        imageEl.alt = displayName;
        imageEl.hidden = false;
        imageEl.classList.remove('dfn-hidden');
        if (fallbackEl) {
          fallbackEl.hidden = true;
          fallbackEl.classList.add('dfn-hidden');
        }
      } else {
        imageEl.removeAttribute('src');
        imageEl.hidden = true;
        imageEl.classList.add('dfn-hidden');
        if (fallbackEl) {
          fallbackEl.hidden = false;
          fallbackEl.classList.remove('dfn-hidden');
        }
      }

      imageEl.onerror = () => {
        imageEl.hidden = true;
        imageEl.classList.add('dfn-hidden');
        if (fallbackEl) {
          fallbackEl.hidden = false;
          fallbackEl.classList.remove('dfn-hidden');
        }
      };
    }

    if (context.dataset.dfnCollectionContextReady !== 'true') {
      context.dataset.dfnCollectionContextReady = 'true';
      if (changeButton) changeButton.addEventListener('click', openFinderFromCollection);
      if (clearButton) clearButton.addEventListener('click', clearSavedVehicle);
    }

    if (currentFamilyLink) {
      const familySlug = currentFamilySlug(config);
      if (familySlug) {
        currentFamilyLink.href = buildVehicleResultsUrl(config, vehicle, familySlug);
        currentFamilyLink.textContent = formatText(config.currentFamilyLabel, { family: currentFamilyName(config, familySlug) });
        currentFamilyLink.hidden = false;
        currentFamilyLink.classList.remove('dfn-hidden');
      } else {
        currentFamilyLink.removeAttribute('href');
        currentFamilyLink.hidden = true;
        currentFamilyLink.classList.add('dfn-hidden');
      }
    }

    context.hidden = false;
    context.classList.remove('dfn-hidden');
    renderCollectionFamilyNav(root, vehicle, config).catch(() => hideCollectionFamilyNav(root));
  }

  async function apiFetch(path) {
    const cacheKey = path;
    const cached = requestCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value;
    }

    const persisted = readSessionCache(cacheKey);
    if (persisted) {
      requestCache.set(cacheKey, { value: persisted, expiresAt: Date.now() + REQUEST_CACHE_TTL });
      return persisted;
    }

    if (inflightRequests.has(cacheKey)) {
      return inflightRequests.get(cacheKey);
    }

    const request = (async () => {
      const response = await fetch(`${API_BASE}${path}`);
      let json = null;

      try {
        json = await response.json();
      } catch (_) {
        json = null;
      }

      if (!response.ok || !json || json.success === false) {
        throw new Error((json && json.error) || `API error ${response.status}`);
      }

      const data = json.data;
      requestCache.set(cacheKey, { value: data, expiresAt: Date.now() + REQUEST_CACHE_TTL });
      writeSessionCache(cacheKey, data, REQUEST_CACHE_TTL);
      return data;
    })().finally(() => inflightRequests.delete(cacheKey));

    inflightRequests.set(cacheKey, request);
    return request;
  }

  async function getCompatibility(vehicle, config) {
    const id = vehicleId(vehicle);
    if (!id) return { handles: new Set(), skus: new Set() };

    const params = new URLSearchParams({
      vehicleId: String(id),
      storeId: config.storeId,
      tecDocProfile: config.profile,
      lang: config.language
    });

    const key = `compatible-skus:${params.toString()}`;
    if (!compatibilityCache.has(key)) {
      compatibilityCache.set(key, apiFetch(`/compatible-skus?${params.toString()}`));
    }

    const result = await compatibilityCache.get(key);
    const compatibility = {
      handles: new Set(),
      skus: new Set()
    };

    const skus = (result && (result.skus || result.Skus)) || [];
    const handles = (result && (result.handles || result.Handles)) || [];

    skus.forEach((sku) => {
      skuAliases(sku).forEach((alias) => compatibility.skus.add(alias));
    });
    handles.forEach((value) => {
      const handle = normalizeHandle(value);
      if (handle) compatibility.handles.add(handle);
    });

    return compatibility;
  }

  async function getVehicleFamilies(vehicle, config) {
    const id = vehicleId(vehicle);
    if (!id) return [];

    const params = new URLSearchParams({
      vehicleId: String(id),
      storeId: config.storeId,
      tecDocProfile: config.profile,
      lang: config.language,
      page: '1',
      limit: '12'
    });

    const key = `vehicle-families:${params.toString()}`;
    if (!familyCache.has(key)) {
      familyCache.set(key, apiFetch(`/vehicle-results?${params.toString()}`));
    }

    const result = await familyCache.get(key);
    return result && Array.isArray(result.families) ? result.families : [];
  }

  async function renderCollectionFamilyNav(root, vehicle, config) {
    const nav = root && root.querySelector('[data-dfn-collection-family-nav]');
    if (!nav || !vehicleMatchesConfig(vehicle, config)) {
      hideCollectionFamilyNav(root);
      return;
    }

    const requestKey = `${vehicleId(vehicle) || ''}:${config.storeId}:${config.profile}`;
    nav.dataset.dfnFamilyRequestKey = requestKey;

    const families = await getVehicleFamilies(vehicle, config);
    if (nav.dataset.dfnFamilyRequestKey !== requestKey) return;

    const visibleFamilies = families
      .filter((family) => family && family.family && Number(family.total || 0) > 0)
      .slice(0, 6);

    if (visibleFamilies.length <= 1) {
      hideCollectionFamilyNav(root);
      return;
    }

    const currentCollection = normalize(config.currentCollection);
    const allUrl = buildVehicleResultsUrl(config, vehicle, '');
    const links = visibleFamilies.map((family) => {
      const familySlug = String(family.family || '').trim();
      const isCurrent = currentCollection === normalize(familySlug);
      return `
        <a class="dfn-collection-family-nav__link" href="${esc(buildVehicleResultsUrl(config, vehicle, familySlug))}" ${isCurrent ? 'aria-current="true"' : ''}>
          <span>${esc(family.familyName || familySlug)}</span>
          <small>${esc(countLabel(config, family.total || 0))}</small>
        </a>`;
    }).join('');

    nav.innerHTML = `
      <span class="dfn-collection-family-nav__label">${esc(config.moreCategoriesLabel)}</span>
      <div class="dfn-collection-family-nav__links">
        <a class="dfn-collection-family-nav__link dfn-collection-family-nav__link--all" href="${esc(allUrl)}">
          <span>${esc(config.allPartsLabel)}</span>
          <small>${esc(config.familyAllResultsLabel)}</small>
        </a>
        ${links}
      </div>`;
    nav.hidden = false;
    nav.classList.remove('dfn-hidden');
  }

  function getCards(root, options) {
    const settings = options || {};
    const cards = new Set(qsa(root, CARD_SELECTOR));

    qsa(root, 'a.js-prod-link[href*="/products/"], a.card-link[href*="/products/"], a[href*="/products/"]').forEach((link) => {
      const card = resolveCardFromLink(link);
      if (card) cards.add(card);
    });

    const cardList = Array.from(cards);
    return cardList.filter((card) => {
      if (card.closest('[data-dfn-results-root="true"]')) return false;
      if (card.closest('[data-dfn-fitment-ignore="true"]')) return false;
      if (cardList.some((other) => isPreferredNestedCard(card, other))) return false;
      return !settings.excludeCollectionCards || !card.closest(COLLECTION_ROOT_SELECTOR);
    });
  }

  function clearCards(root, options) {
    getCards(root, options).forEach((card) => {
      card.dataset.dfnVehicleCompatible = '';
      card.dataset.dfnVehicleMatch = '';
      const badge = card.querySelector(BADGE_SELECTOR);
      if (badge) {
        badge.hidden = true;
        badge.innerHTML = '';
      }
    });
  }

  function markCards(root, compatibility, label, options) {
    const compatibleSkus = compatibility instanceof Set
      ? compatibility
      : compatibility && compatibility.skus instanceof Set
        ? compatibility.skus
        : new Set();
    const compatibleHandles = compatibility && compatibility.handles instanceof Set
      ? compatibility.handles
      : new Set();

    getCards(root, options).forEach((card) => {
      const aliases = cardSkuAliases(card);
      const handles = cardHandles(card);
      const matchedSku = aliases.find((sku) => compatibleSkus.has(sku)) || '';
      const matchedHandle = handles.find((handle) => compatibleHandles.has(handle)) || '';
      const isCompatible = Boolean(matchedSku || matchedHandle);

      card.dataset.dfnVehicleCompatible = isCompatible ? 'true' : 'false';
      card.dataset.dfnVehicleMatch = matchedSku ? `sku:${matchedSku}` : matchedHandle ? `handle:${matchedHandle}` : '';

      const badge = findOrCreateBadge(card);
      if (!badge) return;

      if (isCompatible) {
        badge.hidden = false;
        badge.innerHTML = `<span>${esc(label)}</span>`;
      } else {
        badge.hidden = true;
        badge.innerHTML = '';
      }
    });
  }

  function vehicleMatchesConfig(vehicle, config) {
    if (!vehicle) return false;
    if (vehicle.storeId && vehicle.storeId !== config.storeId) return false;
    if (vehicle.tecDocProfile && vehicle.tecDocProfile !== config.profile) return false;
    return true;
  }

  async function applyFitment(root, options) {
    const vehicle = getSavedVehicle();
    const config = getConfig(root, vehicle);
    const activeVehicle = vehicleMatchesConfig(vehicle, config) ? vehicle : null;
    renderCollectionVehicleContext(root, activeVehicle, config);

    if (!activeVehicle) {
      hideCollectionFamilyNav(root);
      clearCards(root, options);
      return;
    }

    try {
      const compatibility = await getCompatibility(activeVehicle, config);
      markCards(root, compatibility, config.label, options);
    } catch (_) {
      clearCards(root, options);
    }
  }

  function mutationIncludesCollectionRoots(mutations) {
    return mutations.some((mutation) => {
      return Array.from(mutation.addedNodes || []).some((node) => {
        if (!node || node.nodeType !== 1) return false;
        return node.matches(FITMENT_ROOT_SELECTOR) || node.querySelector(FITMENT_ROOT_SELECTOR);
      });
    });
  }

  function mutationAddsProductCards(mutations) {
    return mutations.some((mutation) => {
      return Array.from(mutation.addedNodes || []).some((node) => {
        if (!node || node.nodeType !== 1) return false;
        if (node.matches(BADGE_SELECTOR) || node.closest(BADGE_SELECTOR)) return false;

        return node.matches(CARD_SELECTOR)
          || node.matches('.js-pagination-result')
          || node.matches('a[href*="/products/"]')
          || node.querySelector(`${CARD_SELECTOR}, .js-pagination-result, a[href*="/products/"]`);
      });
    });
  }

  function applyCollectionFitment(root, options) {
    return applyFitment(root, options);
  }

  function scheduleFitment(root, options) {
    if (!root || scheduledRoots.has(root)) return;
    scheduledRoots.add(root);
    window.requestAnimationFrame(() => {
      scheduledRoots.delete(root);
      applyCollectionFitment(root, options);
    });
  }

  function activateRoot(root) {
    if (!root || root.dataset.dfnCollectionFitmentReady === 'true') return;
    root.dataset.dfnCollectionFitmentReady = 'true';
    scheduleFitment(root);

    const observer = new MutationObserver((mutations) => {
      if (mutationAddsProductCards(mutations)) {
        scheduleFitment(root);
      }
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function initRoot(root) {
    if (!root || root.dataset.dfnCollectionFitmentObserved === 'true') return;
    root.dataset.dfnCollectionFitmentObserved = 'true';

    if (typeof window.initLazyScript === 'function') {
      window.initLazyScript(root, () => activateRoot(root), 1000);
    } else {
      activateRoot(root);
    }
  }

  function initCollectionObserver() {
    if (document.documentElement.dataset.dfnCollectionObserverReady === 'true') return;
    document.documentElement.dataset.dfnCollectionObserverReady = 'true';
    let initScheduled = false;

    function scheduleRootScan() {
      if (initScheduled) return;
      initScheduled = true;
      window.requestAnimationFrame(() => {
        initScheduled = false;
        qsa(document, FITMENT_ROOT_SELECTOR).forEach(initRoot);
      });
    }

    const observer = new MutationObserver((mutations) => {
      if (mutationIncludesCollectionRoots(mutations)) {
        scheduleRootScan();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initAll() {
    initCollectionObserver();
    qsa(document, FITMENT_ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('dfn:vehicle-selected', () => {
    qsa(document, FITMENT_ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.dfnCollectionFitmentReady === 'true') scheduleFitment(root);
    });
  });
  document.addEventListener('dfn:vehicle-cleared', () => {
    qsa(document, FITMENT_ROOT_SELECTOR).forEach((root) => {
      hideCollectionVehicleContext(root);
      clearCards(root);
    });
  });
})();
